import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import {
  getBidderForEvent,
  deleteBidderForEvent,
  getBidderByUuid,
  NON_INDIVIDUAL_BIDDER_TYPES,
} from "services";
import { ColumnDef } from "@tanstack/react-table";
import BidderDataTable from "./BiddersDataTable";
import { EllipsisVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../../../shadcn/components/ui/dropdown-menu";
import StatusDropdown from "../events/StatusDropDown";
import { useRouter } from "next/router";
import { getBidderParticipation } from "services/bidder";
import { LoadingSpinner } from "../../../icons/LoadingSpinner";

export type tableData = {
  [x: string]: any;
  id: string;
};

interface props {
  eventData: any;
}

export default function BidderDetailsWrapper({ eventData }: props) {
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteBidderUuid, setDeleteBidderUuid] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const router = useRouter();

  const statuses = ["All", "Pending", "Verified", "Rejected"];

  const handleGetAuctionEvents = useCallback(
    async (shouldShowNextPage: boolean, status: string | null = null) => {
      if (isFetchingMore || (shouldShowNextPage && page >= totalPages)) {
        return;
      }

      setIsFetchingMore(true);
      const currentPage = shouldShowNextPage ? page + 1 : 1;
      try {
        if (!eventData?.uuid) {
          return;
        }

        if (!shouldShowNextPage) setIsLoading(true);
        const params = {
          page: currentPage,
          filterBy: {
            attribute: "access_type",
            op: "eq",
            value: "primary",
          },
        };

        const res: any = await getBidderForEvent(params, eventData?.uuid);

        setTotalPages(res.meta.total_page);
        setPage(currentPage);

        setEvents((prev) =>
          shouldShowNextPage ? [...prev, ...res.data] : res.data,
        );

        if (currentPage < totalPages) {
          handleGetAuctionEvents(true, status);
        }
      } catch (error) {
        console.error("Error fetching auction events:", error);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [page, totalPages, isFetchingMore, selectedStatus, eventData, events],
  );

  useEffect(() => {
    handleGetAuctionEvents(false);
  }, [eventData]);

  const FilterLeft = () => {
    return (
      <div className="flex gap-4">
        <StatusDropdown
          statuses={statuses}
          selectedStatus={selectedStatus}
          onSelect={setSelectedStatus}
        />
      </div>
    );
  };

  // Format session time
  const formatSessionTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const columns: ColumnDef<tableData>[] = [
    {
      accessorKey: "email",
      header: () => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          Email
        </Button>
      ),
      cell: ({ row }) => (
        <div className="lowercase min-w-16">{row.getValue("email")}</div>
      ),
    },
    {
      accessorKey: "kyc_verified",
      header: () => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          KYC Verified
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalise min-w-16">
          {row.getValue("kyc_verified") === true ? "Yes" : "No"}
        </div>
      ),
    },
    {
      accessorKey: "emd_amount_paid",
      header: () => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          EMD Amount paid
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalise min-w-16">
          {row.getValue("emd_amount_paid") === true ? "Yes" : "No"}
        </div>
      ),
    },
    {
      accessorKey: "agreement_signed",
      header: () => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          Agreement Signed
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalise min-w-16">
          {row.getValue("agreement_signed") === true ? "Yes" : "No"}
        </div>
      ),
    },
    {
      accessorKey: "application_form_filled",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          Application Form Filled
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalise min-w-16">
          {row.getValue("application_form_filled") === true ? "Yes" : "No"}
        </div>
      ),
    },
    {
      accessorKey: "created_by",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          KYC Type
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalise min-w-16">
          {row.getValue("created_by") === "banker" ? "Physical" : "Online"}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div
          className={`capitalize px-2 py-1 rounded-full text-center text-sm font-normal  ${
            row.getValue("status") === "pending"
              ? "bg-yellow-100 text-yellow-700"
              : row.getValue("status") === "verified"
              ? "bg-[#F2FFEA] text-[#4DBB3E]"
              : "bg-red-50 text-red-500"
          }`}
        >
          {row.getValue("status")}
        </div>
      ),
    },
    {
      accessorKey: "bidder_relations",
      header: `Jt. Bidders Count`,
      cell: ({ row }) => {
        const joint_bidders_data = row.getValue("bidder_relations");
        return (
          <div
            className={`capitalize px-2 py-1 rounded-full text-start text-xs font-normal min-w-32`}
          >
            {Array.isArray(joint_bidders_data) && joint_bidders_data?.length > 0
              ? joint_bidders_data?.length
              : "0"}
          </div>
        );
      },
    },
    {
      accessorKey: "duration",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
        >
          Duration
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalise min-w-16">
          {row.getValue("duration")
            ? formatSessionTime(row.getValue("duration"))
            : "N/A"}
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const buttonText = "View Details";
        let user_type =
          row.original?.meta?.user_details?.user_type?.value ?? "";
        let non_individual_types = NON_INDIVIDUAL_BIDDER_TYPES;
        return (
          <div className="flex gap-2">
            <Button
              onClick={() => {
                if (row.original.is_joint === true)
                  router.push(
                    `/dashboard/bidders/view/?id=${row.original.uuid}&bidder_type=joint`,
                  );
                else if (non_individual_types.includes(user_type))
                  router.push(
                    `/dashboard/bidders/view/?id=${row.original.uuid}&bidder_type=${user_type}`,
                  );
                else
                  router.push(
                    `/dashboard/bidders/view/?id=${row.original.uuid}&bidder_type=individual`,
                  );
              }}
              className={`bg-gray-100 hover:bg-gray-200 text-black rounded-[8px] hover:cursor-pointer`}
            >
              {buttonText}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <EllipsisVertical className="my-auto hover:cursor-pointer" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white rounded-[8px]"
              >
                <DropdownMenuItem
                  disabled={
                    row.getValue("created_by") !== "banker" ||
                    eventData?.status === "end" ||
                    eventData?.status === "rejected" ||
                    eventData?.status === "cancelled"
                  }
                  onClick={() => {
                    if (row.original.is_joint === true)
                      router.push(
                        `/dashboard/bidders/edit/?id=${row.original.uuid}&for_event=${eventData?.listing_id}&bidder_type=joint`,
                      );
                    else if (non_individual_types.includes(user_type))
                      router.push(
                        `/dashboard/bidders/edit/?id=${row.original.uuid}&for_event=${eventData?.listing_id}&bidder_type=${user_type}`,
                      );
                    else
                      router.push(
                        `/dashboard/bidders/edit/?id=${row.original.uuid}&for_event=${eventData?.listing_id}&bidder_type=individual`,
                      );
                  }}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={
                    eventData?.status === "end" ||
                    eventData?.status === "rejected" ||
                    eventData?.status === "cancelled"
                  }
                  onClick={async () => {
                    setDeleteBidderUuid(row.original.uuid);
                    setShowDeleteModal(true);
                  }}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const filteredRecords =
    selectedStatus && selectedStatus !== "All"
      ? events.filter(
          (record: any) =>
            record.status === selectedStatus.toLowerCase() &&
            (record.digio_request_status === "approved" ||
              record.created_by === "banker"),
        )
      : events.filter(
          (record: any) =>
            record.digio_request_status === "approved" ||
            record.created_by === "banker",
        );

  const DeleteBidderModal = () => {
    const [bidder, setBidder] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [deleteText, setDelateText] = useState(
      "Are you sure you want to remove this bidder?",
    );
    useEffect(() => {
      (async () => {
        setLoading(true);
        const data: any = await getBidderByUuid(deleteBidderUuid);
        setBidder(data);
        if (data?.is_joint) {
          setDelateText(
            "The selected bidder is part of a joint participation. Deleting this bidder will also remove all associated joint bidders. Do you want to proceed?",
          );
        }
        setLoading(false);
      })();
    }, []);

    const handleBidderDeleteWithJoints = async () => {
      setLoading(true);
      try {
        if (bidder?.is_joint) {
          const res: any = await getBidderParticipation({
            bidder_uuid: bidder?.uuid,
          });
          console.log("res: ", res);
          if (res?.length > 0) {
            const res2: any = await getBidderParticipation({
              primary_bidder_uuid: res?.[0]?.primary_bidder_uuid,
            });
            if (res2?.length > 0) {
              for (let i = 0; i < res2.length; i++) {
                const deleteBidderUuid = res2[i]?.bidder_uuid;
                await deleteBidderForEvent(deleteBidderUuid);
              }
              router.reload();
              await new Promise((r) => setTimeout(r, 2000));
            }
          } else {
            router.reload();
          }
        } else {
          if (!deleteBidderUuid) return;
          setIsLoading(true);
          const res = await deleteBidderForEvent(deleteBidderUuid);
          console.log("res: ", res);
          if (res) {
            router.reload();
          }
        }
      } catch (error) {
        console.error("Error while deleting bidder:", error);
      } finally {
        setIsLoading(false);
        setLoading(false);
      }
    };
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <div className="bg-white shadow-2xl  w-full max-w-lg p-6">
          <div className="w-full flex justify-center items-center  pb-3">
            <p className="font-openSans font-semibold text-center text-web-body-lg leading-5 text-brand-bgBlack">
              Remove Bidder
            </p>
          </div>

          <div className={` relative w-full text-center`}>
            {loading ? "Processing the request... Please wait!!" : deleteText}
          </div>

          <div className="w-full flex space-x-3 justify-center mt-6">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                <Button
                  disabled={isLoading || loading}
                  className=" w-full lg:w-fit"
                  onClick={handleBidderDeleteWithJoints}
                >
                  Yes
                </Button>
                <Button
                  disabled={isLoading || loading}
                  className="w-full lg:w-fit "
                  onClick={() => {
                    setShowDeleteModal(false);
                  }}
                >
                  No
                </Button>{" "}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <BidderDataTable
        data={filteredRecords}
        columns={columns}
        filterComponent={<FilterLeft />}
        loading={isLoading}
      />
      {showDeleteModal && <DeleteBidderModal />}
    </div>
  );
}
